import { EditOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StationeryStatus, Status, StepStatus } from "../../enum/common";
import { DepartmentCode } from "../../enum/roles";
import { IQueryStationeryPeriod, IStationeryPeriod } from "../../models/Hrm";
import {
  useCloseStationeryPeriod,
  useOpenStationeryPeriod,
} from "../../query/HrmQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";
import { getStationeryTag, getStatusTag } from "../filter/FilterEnum";

interface HrmStationeryTableProps {
  data?: IStationeryPeriod[];
  isLoading: boolean;
  filter: IQueryStationeryPeriod;
  setFilter: React.Dispatch<React.SetStateAction<IQueryStationeryPeriod>>;
  isHrmView?: boolean;
  onReview?: (id: number, isApprove: boolean) => void;
  onEdit?: (record: IStationeryPeriod) => void;
}

export const HrmStationeryTable: React.FC<HrmStationeryTableProps> = ({
  data,
  isLoading,
  filter,
  setFilter,
  isHrmView = false,
  onReview,
  onEdit,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const isDirector = user?.user.department.departmentCode?.includes(
    DepartmentCode.CEO,
  );
  const mutationClose = useCloseStationeryPeriod();
  const mutationOpen = useOpenStationeryPeriod();

  const handleCloseStationery = (id: number) => {
    try {
      mutationClose.mutate(id);
      notify.success("Đóng kỳ mua văn phòng phẩm thành công");
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      notify.error(
        err.response?.data.message || "Lỗi khi đóng kỳ mua văn phòng phẩm ",
      );
    }
  };
  const handleOpenStationery = async (id: number) => {
    try {
      await mutationOpen.mutateAsync(id);
      notify.success("Đóng kỳ mua văn phòng phẩm thành công");
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      notify.error(
        err.response?.data.message || "Lỗi khi đóng kỳ mua văn phòng phẩm ",
      );
    }
  };

  const columns: ColumnsType<IStationeryPeriod> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "Tên",
      dataIndex: "name",
      width: 200,
    },
    {
      title: "Thời gian",
      key: "dateRange",
      width: 200,
      render: (_, record) =>
        `${dayjs(record.startDate).format("DD/MM/YYYY HH:mm")} -> ${dayjs(record.endDate).format("DD/MM/YYYY HH:mm")}`,
    },
    {
      title: filter.periodStatus
        ? `Trạng thái (${getStationeryTag(filter.periodStatus as StationeryStatus).label})`
        : "Trạng thái",
      dataIndex: "periodStatus",
      width: 160,
      render: (val) => (
        <Tag color={getStationeryTag(val).color}>
          {getStationeryTag(val).label}
        </Tag>
      ),
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Select
            allowClear
            placeholder="Chọn trạng thái"
            value={filter.periodStatus}
            className="mb-2"
            style={{ width: 160 }}
            onChange={(val) =>
              setFilter((prev: any) => ({
                ...prev,
                periodStatus: val,
                page: 1,
              }))
            }
            options={[
              { label: "Mở", value: StationeryStatus.OPEN },
              { label: "Đóng", value: StationeryStatus.CLOSED },
              { label: "Sắp diễn ra", value: StationeryStatus.UPCOMING },
            ]}
          />
        </div>
      ),
    },
    {
      title: filter.approvalStatus
        ? `Trạng thái phê duyệt (${getStatusTag(filter.approvalStatus as unknown as StepStatus).label})`
        : "Trạng thái phê duyệt ",
      dataIndex: "approvalStatus",
      width: 180,
      render: (val) => (
        <Tag color={getStatusTag(val).color}>{getStatusTag(val).label}</Tag>
      ),
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Select
            allowClear
            placeholder="Chọn trạng thái"
            value={filter.approvalStatus}
            className="mb-2"
            style={{ width: 160 }}
            onChange={(val) =>
              setFilter((prev: any) => ({
                ...prev,
                approvalStatus: val,
                page: 1,
              }))
            }
            options={[
              { label: "Đã phê duyệt", value: Status.APPROVED },
              { label: "Chờ phê duyệt", value: Status.PENDING },
              { label: "Từ chối", value: Status.REJECTED },
            ]}
          />
        </div>
      ),
    },

    {
      title: "Thao tác",
      width: 150,
      align: "center",
      render: (_, record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => navigate(String(record.id))}
              disabled={
                !isHrmView &&
                record.approvalStatus !==
                  (Status.APPROVED ||
                    record.periodStatus === StationeryStatus.CLOSED)
              }
            >
              Xem chi tiết
            </Button>
            {isHrmView && onEdit && (
              <Button
                type="text"
                icon={<EditOutlined className="text-blue-500" />}
                onClick={() => onEdit(record)}
              />
            )}
          </Space>
        );
      },
    },
    ...(isHrmView && isDirector
      ? [
          {
            title: "Duyệt đơn",
            width: 150,
            align: "center" as const,
            render: (_: any, record: IStationeryPeriod) => (
              <Space>
                <Popconfirm
                  title="Từ chối đơn?"
                  description="Bạn có chắc muốn từ chối đơn này?"
                  okText="Đồng ý"
                  cancelText="Hủy"
                  onConfirm={() => onReview?.(record.id, false)}
                  disabled={record.approvalStatus !== Status.PENDING}
                >
                  <Button
                    danger
                    size="small"
                    className="p-3"
                    disabled={record.approvalStatus !== Status.PENDING}
                  >
                    Từ chối
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="Phê duyệt đơn?"
                  description="Xác nhận phê duyệt đơn này?"
                  okText="Phê duyệt"
                  cancelText="Hủy"
                  onConfirm={() => onReview?.(record.id, true)}
                  disabled={record.approvalStatus !== Status.PENDING}
                >
                  <Button
                    type="primary"
                    size="small"
                    className="p-3"
                    disabled={record.approvalStatus !== Status.PENDING}
                  >
                    Phê duyệt
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
    ...(isHrmView
      ? [
          {
            title: "Mở kỳ",
            width: 150,
            align: "center" as const,
            render: (_: any, record: IStationeryPeriod) => (
              <Space>
                {record.periodStatus === StationeryStatus.CLOSED ? (
                  <Popconfirm
                    title="Mở kỳ mua văn phòng phẩm?"
                    description="Xác nhận mở kỳ này?"
                    okText="Mở"
                    cancelText="Hủy"
                    onConfirm={() => handleOpenStationery(record.id)}
                  >
                    <Button type="primary" size="small" className="p-3">
                      Mở
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="Đóng kỳ mua văn phòng phẩm?"
                    description="Bạn có chắc muốn đóng kỳ này?"
                    okText="Đồng ý"
                    cancelText="Hủy"
                    onConfirm={() => handleCloseStationery(record.id)}
                  >
                    <Button danger size="small" className="p-3">
                      Đóng
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      rowKey="id"
      scroll={{ x: 1000 }}
      columns={columns}
      dataSource={data || []}
      loading={isLoading}
      pagination={{
        current: filter.page,
        pageSize: filter.limit,
        onChange: (page, pageSize) => {
          setFilter((prev) => ({
            ...prev,
            page,
            limit: pageSize,
          }));
        },
      }}
    />
  );
};
